// Confirmation « lecture seule » d'une conversation, portée par le singleton de messagerie.
//
// CONTEXTE. Le serveur répond 409 CONVERSATION_READ_ONLY à l'envoi dans une conversation privée dont l'interlocuteur n'existe
// plus (compte effacé, RGPD). Ce refus est une CONFIRMATION de l'état réel : on la retient pour l'identifiant de la conversation,
// et on la réapplique à tout objet de conversation reconstruit depuis des données serveur POTENTIELLEMENT PÉRIMÉES
// (liste chargée avant l'effacement : `readOnly: false`).
//
// POURQUOI C'EST PERMANENT. Une ligne de participation supprimée par l'effacement ne revient jamais : l'état « lecture seule »
// d'une conversation ne redevient pas « écriture » pendant la session. Une actualisation qui annonce `readOnly: false` après
// la confirmation ne peut donc être que périmée, et ne doit jamais rouvrir la saisie.
//
// CONDITIONS DE RÉINITIALISATION (les seules) :
//   - resetMessagingState() : déconnexion, perte d'authentification, changement d'utilisateur ;
//   - suppression de la conversation par l'utilisateur (deleteConversation) ;
//   - rechargement de la page (l'état est reconstruit depuis le serveur, qui annonce alors la vérité).
// Fermer la fenêtre, minimiser, recharger la liste ou resynchroniser la conversation ne la réinitialisent PAS.
//
// Fonctions pures : elles ne touchent ni au singleton ni au DOM (readOnlyConfirmation.test.js).

const key = (id) => (id === null || id === undefined ? '' : String(id))

/** Retient la confirmation. @returns {boolean} true si elle est nouvelle. */
export function confirmReadOnly(confirmed, conversationId) {
  const k = key(conversationId)
  if (!k || confirmed.has(k)) return false
  confirmed.add(k)
  return true
}

export function isReadOnlyConfirmed(confirmed, conversationId) {
  return confirmed.has(key(conversationId))
}

/** Oublie la confirmation d'UNE conversation (suppression par l'utilisateur). */
export function forgetReadOnly(confirmed, conversationId) {
  return confirmed.delete(key(conversationId))
}

/**
 * Réapplique la confirmation à un objet de conversation issu du serveur. Ne rend jamais la saisie à une conversation
 * confirmée en lecture seule ; ne touche pas aux autres. Renvoie le MÊME objet quand rien ne change.
 */
export function withConfirmedReadOnly(conversation, confirmed) {
  if (!conversation || conversation.readOnly === true) return conversation
  return confirmed.has(key(conversation.id)) ? { ...conversation, readOnly: true } : conversation
}

/** Marque `readOnly: true` dans une liste. Renvoie la MÊME liste quand rien ne change (identité préservée). */
export function markListReadOnly(list, conversationId) {
  const k = key(conversationId)
  let changed = false
  const next = list.map((conversation) => {
    if (key(conversation.id) === k && conversation.readOnly !== true) {
      changed = true
      return { ...conversation, readOnly: true }
    }
    return conversation
  })
  return changed ? next : list
}
